import { useState, useEffect, useCallback } from 'react';

const bodyHeight = (body: HTMLElement) =>
  Math.max(
    body.scrollHeight,
    body.offsetHeight,
    body.clientHeight,
    body.getBoundingClientRect().bottom
  );

// documentElement.clientHeight and scrollHeight are floored by the iframe's
// own viewport (which patron itself controls), so they can't be used as
// content-height proxies — they'd anchor to the iframe's current size and
// prevent it from ever shrinking. offsetHeight and the bounding-rect bottom
// reflect the html element's actual layout box and ARE useful as safety nets
// against body geometry under-reporting in non-standard layouts.
const docElHeight = (docEl: HTMLElement) =>
  Math.max(docEl.offsetHeight, docEl.getBoundingClientRect().bottom);

type UseIframeHeightProps = {
  onRefChange?: (node: HTMLIFrameElement | null) => void;
  styles?: string;
  iframeIsVisible?: boolean;
  disabled?: boolean;
};

const useIframeResize = ({
  onRefChange,
  styles = '',
  iframeIsVisible = true,
  disabled = false,
}: UseIframeHeightProps) => {
  const [hasResized, setHasResized] = useState(false);
  const [height, setHeight] = useState<number>();
  const [iframeElement, setIframeElement] = useState<HTMLIFrameElement | null>(
    null
  );

  const fixHeight = useCallback(() => {
    try {
      if (!iframeElement?.contentWindow || disabled) {
        return;
      }
      const {
        contentWindow: { document },
      } = iframeElement;
      const { body, head, documentElement: docEl } = document;

      let style = document.getElementById('patron-style');
      if (style === null) {
        style = document.createElement('style');
        style.id = 'patron-style';
        head.appendChild(style);
      }

      // Neutralize the iframe html's flex centering and min-height:100vh so
      // measurements reflect true content height rather than being offset by
      // centering or floored by the iframe viewport. Without this, short
      // articles measure to 100vh of the iframe's initial viewport and never
      // shrink to content height.
      style.innerHTML = `${styles}\nhtml { display: block !important; min-height: 0 !important; align-items: normal !important; }`;

      const contentHeight = Math.max(bodyHeight(body), docElHeight(docEl));

      setHasResized(true);
      setHeight((prev) => (prev === contentHeight ? prev : contentHeight));
    } catch (error) {
      console.error(
        'An error occurred while adjusting the iframe height:',
        error
      );
    }
  }, [iframeElement, styles, disabled]);

  useEffect(() => {
    if (iframeElement && !disabled) {
      fixHeight();
      iframeElement.addEventListener('load', fixHeight);
      window.addEventListener('resize', fixHeight);

      return () => {
        iframeElement.removeEventListener('load', fixHeight);
        window.removeEventListener('resize', fixHeight);
      };
    }
  }, [iframeIsVisible, fixHeight, iframeElement, disabled]);

  useEffect(() => {
    if (disabled) return;
    if (!iframeElement?.contentDocument?.body) return;
    const { body, documentElement: docEl } = iframeElement.contentDocument;

    const resizeObserver = new ResizeObserver(fixHeight);
    resizeObserver.observe(body);
    resizeObserver.observe(docEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [fixHeight, iframeElement?.contentDocument?.body, disabled]);

  // Re-measure as each image finishes loading. Images render at height:auto
  // (0px) until decoded, so on a slow connection the iframe's `load` event and
  // the body/documentElement ResizeObserver can both fire before images have
  // their real height — leaving the iframe sized too short. Listening to each
  // not-yet-loaded image guarantees a re-measure once its height is known,
  // regardless of whether the ResizeObserver catches the layout change.
  useEffect(() => {
    if (disabled) return;
    const doc = iframeElement?.contentDocument;
    if (!doc?.body) return;

    const pendingImages = Array.from(doc.images).filter((img) => !img.complete);
    if (pendingImages.length === 0) return;

    pendingImages.forEach((img) => {
      img.addEventListener('load', fixHeight, { once: true });
      img.addEventListener('error', fixHeight, { once: true });
    });

    return () => {
      pendingImages.forEach((img) => {
        img.removeEventListener('load', fixHeight);
        img.removeEventListener('error', fixHeight);
      });
    };
  }, [fixHeight, iframeElement?.contentDocument?.body, disabled]);

  const ref = useCallback(
    (node) => {
      if (node !== null) {
        setIframeElement(node);
      }
      if (onRefChange) {
        onRefChange(node);
      }
    },
    [onRefChange]
  );

  return { height, ref, hasResized };
};

export default useIframeResize;
